import { ed25519 } from "@noble/curves/ed25519.js";
import { secp256k1 } from "@noble/curves/secp256k1.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { base58, createBase58check } from "@scure/base";
import { HDKey } from "@scure/bip32";
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";
import { networks, payments } from "bitcoinjs-lib";
import { HDKey as Slip10Key } from "micro-key-producer/slip10.js";
import { bytesToHex, hexToBytes } from "viem";
import { privateKeyToAddress } from "viem/accounts";
export const KEY_FAMILIES = ["EVM", "TRON", "BITCOIN", "SOLANA"];
export const HOT_WALLET_INDEX = 0;
export const FIRST_USER_INDEX = 1000;
const MAX_INDEX = 2 ** 31 - 1;
const base58check = createBase58check(sha256);
const TRON_ADDRESS_PREFIX = 0x41;
export function assertValidIndex(index) {
    if (!Number.isInteger(index) || index < 0 || index > MAX_INDEX) {
        throw new RangeError(`Invalid derivation index: ${index}`);
    }
}
export function derivationPath(family, index, mode) {
    assertValidIndex(index);
    switch (family) {
        case "EVM":
            return `m/44'/60'/0'/0/${index}`;
        case "TRON":
            return `m/44'/195'/0'/0/${index}`;
        case "BITCOIN":
            return `m/84'/${mode === "mainnet" ? 0 : 1}'/0'/0/${index}`;
        case "SOLANA":
            return `m/44'/501'/${index}'/0'`;
    }
}
// ───────────────────────── Mnemonics ─────────────────────────
/** New 12-word BIP39 mnemonic (128 bits of entropy). */
export function generateMnemonic12() {
    return generateMnemonic(wordlist, 128);
}
/** New 24-word BIP39 mnemonic (256 bits of entropy) — the owner's vault-recovery key (Phase 9c). */
export function generateMnemonic24() {
    return generateMnemonic(wordlist, 256);
}
export function isValidMnemonic(mnemonic) {
    return validateMnemonic(normalizeMnemonic(mnemonic), wordlist);
}
export function normalizeMnemonic(mnemonic) {
    return mnemonic.trim().toLowerCase().split(/\s+/).join(" ");
}
/** BIP39 mnemonic → 64-byte seed. Throws on a bad checksum or unknown word. */
export function mnemonicToSeed(mnemonic, passphrase = "") {
    const normalized = normalizeMnemonic(mnemonic);
    if (!validateMnemonic(normalized, wordlist))
        throw new Error("Invalid mnemonic (wrong word or checksum)");
    return mnemonicToSeedSync(normalized, passphrase);
}
// ───────────────────────── Derivation ─────────────────────────
function deriveSecp256k1(seed, path) {
    const node = HDKey.fromMasterSeed(seed).derive(path);
    if (!node.privateKey || !node.publicKey)
        throw new Error(`Derivation failed for ${path}`);
    return { privateKey: node.privateKey, publicKey: node.publicKey };
}
function evmAddressFromPrivateKey(privateKey) {
    return privateKeyToAddress(bytesToHex(privateKey));
}
export function tronAddressFromEvmAddress(evmAddress) {
    const body = hexToBytes(evmAddress);
    return base58check.encode(Uint8Array.from([TRON_ADDRESS_PREFIX, ...body]));
}
export function deriveKey(seed, family, index, mode) {
    const path = derivationPath(family, index, mode);
    switch (family) {
        case "EVM": {
            const { privateKey } = deriveSecp256k1(seed, path);
            return { family, index, path, privateKey, address: evmAddressFromPrivateKey(privateKey) };
        }
        case "TRON": {
            const { privateKey } = deriveSecp256k1(seed, path);
            const address = tronAddressFromEvmAddress(evmAddressFromPrivateKey(privateKey));
            return { family, index, path, privateKey, address };
        }
        case "BITCOIN": {
            const { privateKey, publicKey } = deriveSecp256k1(seed, path);
            const network = mode === "mainnet" ? networks.bitcoin : networks.testnet; // signet uses testnet params (tb1…)
            const { address } = payments.p2wpkh({ pubkey: publicKey, network });
            if (!address)
                throw new Error("Could not build Bitcoin address");
            return { family, index, path, privateKey, address };
        }
        case "SOLANA": {
            const node = Slip10Key.fromMasterSeed(seed).derive(path);
            return { family, index, path, privateKey: node.privateKey, address: base58.encode(node.publicKeyRaw) };
        }
    }
}
/**
 * Address of a single imported private key (self-custody vault, Phase 9). Same address rules as
 * deriveKey: EVM/Tron from the secp256k1 key, Bitcoin native SegWit (compressed key), Solana
 * from the 32-byte ed25519 seed. Throws on a key that isn't valid for the curve.
 */
export function addressFromPrivateKey(family, privateKey, mode) {
    if (privateKey.length !== 32)
        throw new Error("A private key must be 32 bytes");
    switch (family) {
        case "EVM":
        case "TRON": {
            if (!secp256k1.utils.isValidSecretKey(privateKey))
                throw new Error("Not a valid private key");
            const evm = evmAddressFromPrivateKey(privateKey);
            return family === "EVM" ? evm : tronAddressFromEvmAddress(evm);
        }
        case "BITCOIN": {
            if (!secp256k1.utils.isValidSecretKey(privateKey))
                throw new Error("Not a valid private key");
            const network = mode === "mainnet" ? networks.bitcoin : networks.testnet;
            const { address } = payments.p2wpkh({ pubkey: secp256k1.getPublicKey(privateKey, true), network });
            if (!address)
                throw new Error("Could not build Bitcoin address");
            return address;
        }
        case "SOLANA":
            return base58.encode(ed25519.getPublicKey(privateKey));
    }
}
/** Address only — the private key is wiped before returning. */
export function deriveAddress(seed, family, index, mode) {
    const key = deriveKey(seed, family, index, mode);
    key.privateKey.fill(0);
    return key.address;
}
//# sourceMappingURL=derivation.js.map